import isInsuranceSupported from 'checkout/selectors/request/isInsuranceSupported'
import getCheckoutInsuranceQuotes from 'checkout/selectors/view/getCheckoutInsuranceQuotes'
import getInsuranceItemsView from 'checkout/selectors/view/itemViews/getInsuranceItemsView'
import { max, sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { getIsEnabledLuxPlusDiscountedInsurance, getShouldShowFlightsBeforeLuxPlusUpsell } from 'luxPlus/selectors/featureToggle'
import getIsCheckoutInsuranceFetching from 'checkout/selectors/view/getIsCheckoutInsuranceFetching'
import getCheckoutSelectedQuotes from 'checkout/selectors/view/getCheckoutSelectedQuote'
import { getInsuranceItems } from 'checkout/selectors/view/insurance'
import { checkoutContainsFlightsWithEmbeddedInsurance } from 'checkout/selectors/view/flights'

export const getAvailableInsuranceBenefits = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedInsurance(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => getCheckoutInsuranceQuotes(state),
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => getIsCheckoutInsuranceFetching(state),
  (state: App.State) => getCheckoutSelectedQuotes(state),
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (state: App.State) => getShouldShowFlightsBeforeLuxPlusUpsell(state),
  (
    isEnabledLuxPlusDiscountedInsurance,
    insuranceSupported,
    quotes,
    insuranceItemsView,
    isFetchingInsurance,
    selectedQuotes,
    insuranceItems,
    containsFlightsWithEmbeddedInsurance,
    shouldShowFlightsBeforeLuxPlusUpsell,
  ): App.Checkout.LuxPlusInsuranceBenefits => {
    const maxSavings = max(quotes.map(quote => quote.luxPlusSavings)) ?? 0

    // Applied member prices continue to come from the item view. In the flights-before-upsell
    // preview, a non-member's item view deliberately has no applied member price, so use the
    // selected flight-protection quote to expose the saving available after joining LuxPlus.
    const flightInsuranceItems = insuranceItems.filter(item => item.insuranceType === 'cfmr' && !!item.flightJourneysKeys?.length)
    const shouldPreviewFlightInsuranceMemberPrice = shouldShowFlightsBeforeLuxPlusUpsell && containsFlightsWithEmbeddedInsurance
    const isWaitingForFlightInsuranceQuote = shouldPreviewFlightInsuranceMemberPrice && (
      flightInsuranceItems.length === 0 ||
      flightInsuranceItems.some(item => !selectedQuotes[item.itemId])
    )
    const getAvailableMemberPrice = (item: App.Checkout.InsuranceItemView): number => {
      if (item.totals.memberPrice > 0) return item.totals.memberPrice
      if (!shouldPreviewFlightInsuranceMemberPrice) return 0

      const flightMemberPrice = selectedQuotes[item.item.itemId]?.luxPlusFlightTotal
      return flightMemberPrice && flightMemberPrice > 0 ? flightMemberPrice : 0
    }

    const totalPrice = sum(insuranceItemsView.data, item => item.totals.price)
    const totalMemberPrice = sum(insuranceItemsView.data, getAvailableMemberPrice)
    const totalMemberPriceSavings = sum(insuranceItemsView.data, item => {
      const availableMemberPrice = getAvailableMemberPrice(item)
      return availableMemberPrice > 0 ? Math.max(item.totals.price - availableMemberPrice, 0) : 0
    })

    if (!isEnabledLuxPlusDiscountedInsurance) {
      return {
        isAvailable: false,
        fetching: isWaitingForFlightInsuranceQuote,
        luxPlusDiscountPercentage: 0,
        luxPlusDiscountValueDisplayThreshold: 0,
        totalMemberMaxSavings: 0,
        totalMemberPriceSavings,
        totalPrice,
        totalMemberPrice,
        totalBaseMemberPrice: totalMemberPrice,
      }
    }

    return {
      isAvailable: isEnabledLuxPlusDiscountedInsurance && insuranceSupported,
      fetching: (insuranceSupported && isFetchingInsurance) || isWaitingForFlightInsuranceQuote,
      // Todo: remove the default values (this affects the upsell tile in the order confirmation page)
      luxPlusDiscountPercentage: quotes[0]?.luxPlusDiscountPercentage || 40,
      // Todo: remove the default values (this affects the upsell tile in the order confirmation page)
      luxPlusDiscountValueDisplayThreshold: quotes[0]?.luxPlusDiscountValueDisplayThreshold || 30,
      totalMemberMaxSavings: maxSavings,
      totalMemberPriceSavings,
      totalPrice,
      totalMemberPrice,
      // TODO - revisit this when benefit is available to customers
      totalBaseMemberPrice: totalMemberPrice,
    }
  },
)
